import { useEffect, useRef, useState, useMemo } from "react";
import { saveAs } from "file-saver";

/** Lib Imports */
import { Card } from "primereact/card";
import { Button } from "primereact/button";
import { Calendar } from "primereact/calendar";
import { Dropdown } from "primereact/dropdown";
import { InputText } from "primereact/inputtext";
import MolGrid, { Column } from "@moltech/molui/molgrid";
import moment from "moment";

/** Utils */
import useDebounce from "../../app/hooks/useDebounce";
import { formatDate } from "../../utils/common/dateFormat";
import { formatPhoneNumber } from "../../utils/common/phoneFormat";

/** Components */
import Badges from "../../components/common/badges/Badges";
import NoRecordFound from "../../components/common/no-record-found/NoRecordFound";

/** RTK Query */
import {
  useLazyGetDataQuery,
  usePostFileDataMutation,
  usePostGridDataMutation,
} from "../../app/services/commonApi";

/** CSS Files */
import "./JobReports.scss";

import ChartWrapper from "../../components/common/Charts/ChartWrapper";
import { useMasterData } from "../../app/hooks/useMasterData";
import { MasterDataCacheURL } from "../../enums/CacheURL";

const renderStatus = (row) => (
  <Badges
    label={row.applicantStatusName}
    type={row.applicantStatusName}
    color={row.applicantStatusColor}
  />
);

const JobReports = () => {
  const gridRef = useRef();

  const [selectJobOpening, setSelectJobOpening] = useState(null);
  const [gridData, setGridData] = useState([]);
  const [selectedStartDate, setSelectedStartDate] = useState(null);
  const [selectedEndDate, setSelectedEndDate] = useState(null);
  const [searchCandidateName, setSearchCandidateName] = useState("");
  const debouncedSearch = useDebounce(searchCandidateName, 300);

  const [fetchGridData] = usePostGridDataMutation();
  const [fetchFileData, { isLoading: isExporting }] = usePostFileDataMutation();
  const { data: triggerGetJobOpening } = useMasterData(MasterDataCacheURL.GetAllJobOpening, { publishedOnly: true , page:'JobReports'});
  const [getApplicantsStatusCountByJobOpeningId, { data: statusData, reset: statusCountReset, isLoading: isStatusCountLoading, isFetching: isStatusCountFetching }] = useLazyGetDataQuery();
  const [getApplicantsStatusCountForHiringProgressByJobOpeningId, { data: hiringProgressData, reset: hiringProgressDataReset, isLoading: isHiringProgressDataLoading, isFetching: isHiringProgressDataFetching }] = useLazyGetDataQuery();

  const jobOpeningList = useMemo(() => {
    if (!Array.isArray(triggerGetJobOpening)) return [];

    return triggerGetJobOpening.map(item => ({
      label: item.jobOpeningName,
      value: item.jobOpeningId
    }));
  }, [triggerGetJobOpening]);

  useEffect(() => {
    getJobReportList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearch]);

  const handleClear = () => {
    setSelectJobOpening(null);
    setSelectedEndDate(null);
    setSelectedStartDate(null);
    setGridData([]);
    statusCountReset();
    hiringProgressDataReset();
  };



  const getJobReportList = () => {
    if (!selectJobOpening) return;
    fetchGridData({
      url: "Report/GetCandidateListReportByJob",
      params: {
        jobOpeningId: selectJobOpening,
        startDate: selectedStartDate,
        endDate: selectedEndDate,
        name: debouncedSearch?.trim() || null,
      },
    }).then((res) => {
      const resultArray = res?.data?.data ?? [];
      setGridData(resultArray);
    });

    getApplicantsStatusCountByJobOpeningId({
      url: "/Analytics/GetApplicantsStatusCountByJobOpeningIdChart",
      method: "GET",
      data: {
        jobOpeningId: selectJobOpening,
        startDate: selectedStartDate
          ? moment(selectedStartDate).utc().toISOString()
          : moment.utc("2000-01-01").startOf("day").toISOString(),
        endDate: selectedEndDate
          ? moment(selectedEndDate).utc().toISOString()
          : moment.utc().endOf("day").toISOString(),
      },
    });

    getApplicantsStatusCountForHiringProgressByJobOpeningId({
      url: "/Analytics/GetApplicantsStatusCountForHiringProgressByJobOpeningIdChart",
      method: "GET",
      data: {
        jobOpeningId: selectJobOpening,
        startDate: selectedStartDate
          ? moment(selectedStartDate).utc().toISOString()
          : moment.utc("2000-01-01").startOf("day").toISOString(),
        endDate: selectedEndDate
          ? moment(selectedEndDate).utc().toISOString()
          : moment.utc().endOf("day").toISOString(),
      },
    });
  };

  const handleDateRangeChange = (e) => {
    const [startDate, endDate] = e.value || [];
    setSelectedStartDate(startDate || null);
    setSelectedEndDate(endDate || null);
  };

  const handleSearch = () => {
    if (!selectJobOpening) return;
    getJobReportList();
  };

  const handleExport = async () => {
    if (!selectJobOpening) return;

    const response = await fetchFileData({
      url: "Report/ExportJobReport",
      data: {
        jobOpeningId: selectJobOpening,
        startDate: selectedStartDate,
        endDate: selectedEndDate,
        name: debouncedSearch?.trim() || null,
      },
    });
    const fileData = response.data;
    const blob = new Blob([fileData]);

    // Generate dynamic filename
    const selectedJob = jobOpeningList.find(
      (job) => job.value === selectJobOpening,
    )?.label;
    const startDateStr = selectedStartDate
      ? formatDate(selectedStartDate, "DD-MM-YYYY")
      : "";
    const endDateStr = selectedEndDate
      ? formatDate(selectedEndDate, "DD-MM-YYYY")
      : "";

    let filename = "Job-Report";
    if (selectedJob) filename += `-${selectedJob.replace(/\s+/g, "-")}`;
    if (startDateStr && endDateStr) {
      filename += `-${startDateStr}-to-${endDateStr}`;
    } else if (startDateStr) {
      filename += `-from-${startDateStr}`;
    } else if (endDateStr) {
      filename += `-to-${endDateStr}`;
    }
    filename += ".xlsx";

    saveAs(blob, fileData.filename || filename);
  };

  const header = (
    <div className="custom-header-content mt-4">
      <div className="p-card-title">
        Job Report
        <div className="header-items">
          <InputText
            placeholder="Search by candidate Name"
            value={searchCandidateName}
            disabled={!selectJobOpening}
            onChange={(e) => {
              const value = e.target.value;
              setSearchCandidateName(value);
            }}
          />
          <Button
            label="Export Report"
            icon="pi pi-download"
            className="p-button-sm export-btn  btn secondary-btn"
            onClick={handleExport}
            disabled={isExporting || !gridData.length}
            loading={isExporting}
          />
        </div>
      </div>
    </div>
  );

  return (
    <div className="job-report ">
      <Card className="custom-card height-full-grid-with-searchbar  ">
        <div className="page-header">
          <div className="left-title-part">
            <h2 className="card-title">Job Report</h2>
          </div>
        </div>
        <div className="card-filter">
          <div className="applicant-filter">
            <div className="filter-fields">
              <Dropdown
                name="jobOpening"
                value={selectJobOpening}
                options={jobOpeningList}
                optionLabel="label"
                optionValue="value"
                placeholder="Select Job Position"
                className=""
                showClear
                onChange={(e) => setSelectJobOpening(e.value)}
                required
              />
              <Calendar
                name="startDate"
                label="Date Range"
                placeholder="Select Date Range"
                dateFormat="dd-mm-yy"
                selectionMode="range"
                showIcon
                showButtonBar
                value={
                  selectedStartDate || selectedEndDate
                    ? [selectedStartDate, selectedEndDate]
                    : null
                }
                onChange={handleDateRangeChange}
              />
            </div>
            <div className="btn-group-sec">
              <Button
                label="Search"
                icon="pi pi-search"
                onClick={handleSearch}
                className="p-button-sm  btn secondary-btn"
                disabled={!selectJobOpening}
              />
              <Button
                label="Clear"
                icon="pi pi-times"
                className="p-button-sm p-button-secondary btn secondary-light-btn"
                onClick={handleClear}
              />
            </div>
          </div>
        </div>
      </Card>
      <div className="job-report-top-section">
        <div className="chart-row">

          {/* LEFT - DONUT */}
          <ChartWrapper
            title="Candidate Status Breakdown"
            type="pie"
            data={statusData?.data?.status || []}
            xKey="status"
            yKey="count"
            colorType="status"
            customOptions={{
              tooltip: {
                y: {
                  formatter: (val) =>
                    `${val} candidate${val !== 1 ? "s" : ""}`,
                },
              },
            }}
            loading={isStatusCountLoading || isStatusCountFetching}
          />

          {/* RIGHT - STACKED BAR */}
          <ChartWrapper
            title="Hiring Progress"
            type="bar"
            xKey="label"
            data={hiringProgressData?.data?.hiringProgress || []}
            colorType="status"
            customOptions={{
              chart: {
                stacked: true
              }
            }}
            loading={isHiringProgressDataLoading || isHiringProgressDataFetching}

          />

        </div>
      </div>

      <Card
        className="custom-card mt-4 job-report-card-section"
        header={header}
      >
        {gridData?.length ? (
          <MolGrid
            ref={gridRef}
            value={gridData}
            paginator={false}
            scrollable
            scrollHeight="flex"
            filterDisplay="none"
          >
            <Column field="applicantName" header="Candidate Name" hideColumnMenu={true} />
            <Column field="applicantEmail" header="Email" hideColumnMenu={true} />
            <Column
              field="applicantPhone"
              header="Number"
              body={(row) => formatPhoneNumber(row.applicantPhone)}
              hideColumnMenu={true}
            />
            <Column field="jobOpeningName" header="Position" hideColumnMenu={true} />
            <Column
              field="applicantStatusName"
              header="Status"
              sortable={false}
              body={renderStatus}
              hideColumnMenu={true}
            />
            <Column
              field="applyDate"
              header="Apply Date"
              body={(rowData) => {
                return formatDate(rowData.applyDate);
              }}
              hideColumnMenu={true}
            />
          </MolGrid>
        ) : (
          <NoRecordFound message="" />
        )}
      </Card>
    </div>
  );
};

export default JobReports;